import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, isValid10Digit, to10Digit } from "@/lib/phone";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import {
    MagnifyingGlass, UsersThree, Phone, Envelope, Plus, PencilSimple, Power, UserCirclePlus,
} from "@phosphor-icons/react";

const EMPTY_FORM = { name: "", phone: "", email: "" };

export default function DoctorClinicStaff() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/doctor/clinic-staff")
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        if (!q) return items;
        const l = q.toLowerCase();
        return items.filter((s) =>
            (s.name || "").toLowerCase().includes(l) ||
            (s.phone || "").includes(q) ||
            (s.email || "").toLowerCase().includes(l)
        );
    }, [items, q]);

    // Only the partner doctor (not clinic_staff) sees this manager; the
    // Profile tab is hidden from clinic_staff users at the strip level, so we
    // just no-op here if the role somehow lands an embedded render.
    if (user && user.role !== "doctor") {
        return null;
    }

    const openCreate = () => { setCreating(true); setForm(EMPTY_FORM); };
    const openEdit = (s) => { setEditing(s); setForm({ name: s.name || "", phone: to10Digit(s.phone || ""), email: s.email || "" }); };
    const closeDialog = () => { setCreating(false); setEditing(null); };

    const save = async () => {
        if ((form.name || "").trim().length < 2) { toast.error("Enter a valid name"); return; }
        setSaving(true);
        try {
            if (creating) {
                if (!isValid10Digit(form.phone)) {
                    toast.error("Enter their 10-digit Indian mobile number");
                    setSaving(false);
                    return;
                }
                const r = await api.post("/doctor/clinic-staff", { ...form, phone: toE164(form.phone) });
                setItems((prev) => [r.data, ...prev]);
                toast.success("Team member added");
                setCreating(false);
            } else {
                const r = await api.put(`/doctor/clinic-staff/${editing.id}`, {
                    name: form.name, email: form.email,
                });
                setItems((prev) => prev.map((x) => x.id === editing.id ? { ...x, ...r.data } : x));
                toast.success("Updated");
                setEditing(null);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (s) => {
        const action = s.is_active ? "disable" : "enable";
        if (!await confirmDialog({ title: `${action.charAt(0).toUpperCase() + action.slice(1)} ${s.name}?`, confirmText: "Confirm", tone: "default" })) return;
        try {
            const r = await api.put(`/doctor/clinic-staff/${s.id}/toggle-active`);
            setItems((prev) => prev.map((x) => x.id === s.id ? { ...x, is_active: r.data.is_active } : x));
            toast.success(`${s.name} ${r.data.is_active ? "enabled" : "disabled"}`);
        } catch {
            toast.error(`Failed to ${action}`);
        }
    };

    return (
        <div data-testid="doctor-clinic-staff-page" className="space-y-4">
            {/* Header — description on top, search + add button side-by-side underneath.
                Previous layout had description+controls competing for one row inside
                the narrow Profile shell, which forced the search box to a 220px cap
                and pushed the Add button onto a new line on most desktops. Stacking
                them gives the search room to breathe + keeps the Add button beside it. */}
            <p className="text-xs text-muted-foreground">
                {items.length} {items.length === 1 ? "team member" : "team members"} who can help manage your clinic. They can view cases, appointments, and patients — only you can approve treatment plans.
            </p>
            <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search by name, phone, or email..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 h-9 text-xs w-full"
                        data-testid="doctor-clinic-staff-search"
                    />
                </div>
                <Button onClick={openCreate} size="sm" className="rounded-full shrink-0" data-testid="doctor-clinic-staff-add-btn">
                    <Plus size={14} className="mr-1.5" weight="bold" /> Add member
                </Button>
            </div>

            {loading && <div className="text-muted-foreground">Loading…</div>}

            {!loading && filtered.length === 0 && (
                <div className="bg-card border border-border rounded-2xl p-10 text-center text-muted-foreground">
                    <UsersThree size={32} className="mx-auto mb-2 opacity-50" />
                    <div className="mb-3">No clinic staff yet.</div>
                    <Button variant="outline" onClick={openCreate} data-testid="doctor-clinic-staff-empty-add">
                        <UserCirclePlus size={16} className="mr-1.5" /> Invite your first coordinator
                    </Button>
                </div>
            )}

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {filtered.map((s) => (
                    <div
                        key={s.id}
                        className={`grid grid-cols-12 gap-3 px-5 py-4 border-b border-border last:border-b-0 items-center ${s.is_active ? "" : "opacity-60"}`}
                        data-testid={`doctor-clinic-staff-${s.id}`}
                    >
                        <div className="col-span-12 md:col-span-5 flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm shrink-0">
                                {(s.name || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <div className="font-medium text-sm flex items-center gap-2">
                                    <span className="truncate">{s.name}</span>
                                    {!s.is_active && (
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 uppercase font-semibold">Disabled</span>
                                    )}
                                </div>
                                <div className="text-xs text-muted-foreground">Coordinator</div>
                            </div>
                        </div>
                        <div className="col-span-12 md:col-span-4 min-w-0">
                            <div className="text-sm flex items-center gap-1.5"><Phone size={12} className="text-muted-foreground" /> <span className="truncate">{s.phone}</span></div>
                            {s.email && <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5"><Envelope size={11} /> <span className="truncate">{s.email}</span></div>}
                        </div>
                        <div className="col-span-12 md:col-span-3 flex gap-2 md:justify-end">
                            <Button variant="outline" size="sm" onClick={() => openEdit(s)} data-testid={`doctor-clinic-staff-edit-${s.id}`}>
                                <PencilSimple size={14} className="mr-1" /> Edit
                            </Button>
                            <Button
                                variant="outline" size="sm"
                                onClick={() => toggleActive(s)}
                                className={s.is_active ? "text-rose-600 border-rose-200 hover:bg-rose-50" : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"}
                                data-testid={`doctor-clinic-staff-toggle-${s.id}`}
                            >
                                <Power size={14} className="mr-1" />
                                {s.is_active ? "Disable" : "Enable"}
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={creating || !!editing} onOpenChange={(o) => !o && closeDialog()}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{creating ? "Add clinic staff" : "Edit member"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 mt-2">
                        <div>
                            <Label htmlFor="cs-name" className="text-xs font-semibold">Name</Label>
                            <Input id="cs-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 mt-1" data-testid="doctor-clinic-staff-form-name" />
                        </div>
                        <div>
                            <Label htmlFor="cs-phone" className="text-xs font-semibold">Phone (10-digit Indian mobile)</Label>
                            <div className="h-10 mt-1">
                                <PhoneInput10
                                    id="cs-phone"
                                    value={form.phone}
                                    onChange={(v) => setForm({ ...form, phone: v })}
                                    locked={!creating}
                                    inputClassName="h-10"
                                    testid="doctor-clinic-staff-form-phone"
                                />
                            </div>
                            {!creating && <p className="text-[11px] text-muted-foreground mt-1">Phone cannot be changed.</p>}
                            {creating && <p className="text-[11px] text-muted-foreground mt-1">They'll sign in at /doctor-login with this number (OTP 123456 in dev).</p>}
                        </div>
                        <div>
                            <Label htmlFor="cs-email" className="text-xs font-semibold">Email (optional)</Label>
                            <Input id="cs-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10 mt-1" data-testid="doctor-clinic-staff-form-email" />
                        </div>
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={closeDialog} disabled={saving}>Cancel</Button>
                        <Button onClick={save} disabled={saving} data-testid="doctor-clinic-staff-form-save">
                            {saving ? "Saving…" : (creating ? "Add member" : "Save")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
